/**
 * RecordsUnitList - Filtered unit list for Records sidebar
 *
 * Displays units filtered by FilterContext (companies, groups, models, statuses).
 * Unit clicks navigate to the unit's records page at /records/units/:unitId.
 *
 * Matches the Map sidebar (GroupedUnitList) styling exactly:
 * - Same fonts, sizes, row heights, spacing
 * - Industry grouping for superadmins
 * - Hierarchical group display
 */

import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronsUp,
  ChevronsDown,
  Activity,
  ClipboardList,
  Wrench,
  LogIn,
  MapPin,
} from 'lucide-react';
import { cn, formatLocationShort } from '@/lib/utils';
import { getLocationTextClass } from '@/lib/locationUtils';
import { Separator } from '@/components/ui/separator';
import { useFilters } from '@/contexts/FilterContext';
import { useUnitStatus } from '@/contexts/UnitStatusContext';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { INDUSTRY_TYPE_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import { authenticatedFetch } from '@/lib/api';
import type { Unit, HierarchicalGroupNode, IndustryType } from '@/types';

interface UnitCounts {
  checkin_count: number;
  open_work_orders: number;
  maintenance_due: number;
}

interface RecordsUnitListProps {
  /** Whether the sidebar is in collapsed state */
  collapsed?: boolean;
}

type SortField = 'name' | 'status' | 'checkins' | 'workorders' | 'maintenance' | 'alerts';
type SortDirection = 'asc' | 'desc';

export function RecordsUnitList({ collapsed = false }: RecordsUnitListProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions } = useUserPermissions();
  const { getStatusInfo } = useUnitStatus();
  const {
    units: contextUnits,
    selectedCompanyIds,
    selectedGroupIds,
    selectedIndustryTypes,
    selectedModels,
    selectedStatuses,
    buildHierarchicalGroups,
  } = useFilters();

  // Sort state
  const [sortField, setSortField] = useState<SortField>('status');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string> | null>(null);
  const [collapsedIndustries, setCollapsedIndustries] = useState<Set<IndustryType>>(new Set());

  // Unit record counts for sidebar display
  const [unitCounts, setUnitCounts] = useState<Record<string, UnitCounts>>({});

  // Fetch unit record counts when filters change
  useEffect(() => {
    async function fetchCounts() {
      try {
        const params = new URLSearchParams();
        if (selectedCompanyIds.length > 0) {
          params.set('company_ids', selectedCompanyIds.join(','));
        }
        if (selectedGroupIds.length > 0) {
          params.set('group_ids', selectedGroupIds.join(','));
        }
        const queryString = params.toString();
        const url = `/api/v1/admin/records/units/counts${queryString ? `?${queryString}` : ''}`;
        const response = await authenticatedFetch(url);
        if (response.ok) {
          const data = await response.json();
          setUnitCounts(data.counts || {});
        }
      } catch (error) {
        console.error('[RecordsUnitList] Failed to fetch counts:', error);
      }
    }
    fetchCounts();
  }, [selectedCompanyIds, selectedGroupIds]);

  // Get total count of full Unit objects (before filtering)
  const totalUnitsCount = useMemo(() => {
    return contextUnits.filter((u): u is Unit => 'unit_code' in u && 'location' in u).length;
  }, [contextUnits]);

  // Filter units based on FilterContext selections
  const filteredUnits = useMemo(() => {
    const fullUnits = contextUnits.filter((u): u is Unit => 'unit_code' in u && 'location' in u);

    return fullUnits.filter((unit) => {
      const unitType = unit.industry_type || 'film';
      const matchesType = selectedIndustryTypes.length === 0 || selectedIndustryTypes.includes(unitType);

      const modelPrefix = unit.unit_code.substring(0, 3);
      const matchesModel = selectedModels.length === 0 || selectedModels.includes(modelPrefix);

      let matchesCompany = selectedCompanyIds.length === 0;
      if (!matchesCompany) {
        matchesCompany =
          selectedCompanyIds.includes(unit.company_id) ||
          (selectedCompanyIds.includes('__unassigned__') && !unit.company_id);
      }

      let matchesGroup = selectedGroupIds.length === 0;
      if (!matchesGroup) {
        matchesGroup =
          (unit.primary_group_id && selectedGroupIds.includes(unit.primary_group_id)) ||
          (selectedGroupIds.includes(`__unassigned_${unit.company_id}__`) && !unit.primary_group_id);
      }

      const liveStatus = getStatusInfo(unit.id)?.status || unit.status;
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(liveStatus);

      return matchesType && matchesModel && matchesCompany && matchesGroup && matchesStatus;
    });
  }, [
    contextUnits,
    selectedIndustryTypes,
    selectedModels,
    selectedCompanyIds,
    selectedGroupIds,
    selectedStatuses,
    getStatusInfo,
  ]);

  // Build hierarchical groups for display
  const hierarchicalGroups = useMemo(() => {
    return buildHierarchicalGroups(filteredUnits, permissions);
  }, [filteredUnits, buildHierarchicalGroups, permissions]);

  // Check if there's only one top-level group (single company)
  const hasSingleTopLevelGroup = hierarchicalGroups.length === 1;
  const singleGroupId = hasSingleTopLevelGroup ? hierarchicalGroups[0].id : null;

  // Get industry type for a company-level node from its units (for superadmins)
  const getIndustryForCompany = useCallback((group: HierarchicalGroupNode): IndustryType => {
    if (group.units.length === 0) return 'film';
    const industryCount: Record<string, number> = {};
    group.units.forEach((unit) => {
      const industry = unit.industry_type || 'film';
      industryCount[industry] = (industryCount[industry] || 0) + 1;
    });
    let maxCount = 0;
    let primaryIndustry: IndustryType = 'film';
    Object.entries(industryCount).forEach(([industry, count]) => {
      if (count > maxCount) {
        maxCount = count;
        primaryIndustry = industry as IndustryType;
      }
    });
    return primaryIndustry;
  }, []);

  // Group hierarchical nodes by industry type (for superadmins only)
  const groupedByIndustry = useMemo(() => {
    if (!permissions.canSeeCompanies) return null;

    const grouped: Record<IndustryType, HierarchicalGroupNode[]> = {
      film: [],
      event: [],
      gov: [],
      utility: [],
      relief: [],
    };

    hierarchicalGroups.forEach((group) => {
      if (group.level === 'company') {
        const industry = getIndustryForCompany(group);
        grouped[industry].push(group);
      } else {
        grouped['film'].push(group);
      }
    });

    return grouped;
  }, [hierarchicalGroups, permissions.canSeeCompanies, getIndustryForCompany]);

  // Initialize expanded groups - expand all by default
  useEffect(() => {
    if (hierarchicalGroups.length > 0 && expandedGroups === null) {
      const allIds = new Set<string>();
      const collectIds = (nodes: HierarchicalGroupNode[]) => {
        nodes.forEach((node) => {
          allIds.add(node.id);
          if (node.children) collectIds(node.children);
        });
      };
      collectIds(hierarchicalGroups);
      setExpandedGroups(allIds);
    }
  }, [hierarchicalGroups, expandedGroups]);

  // Sort units within a group
  const sortUnits = useCallback(
    (groupUnits: Unit[]) => {
      return [...groupUnits].sort((a, b) => {
        let comparison = 0;

        switch (sortField) {
          case 'status': {
            const statusPriority: Record<string, number> = {
              active: 1,
              maintenance: 2,
              off_rent: 3,
              inactive: 4,
            };
            const aStatus = a.operationalStatus || 'active';
            const bStatus = b.operationalStatus || 'active';
            const aPriority = statusPriority[aStatus] || 99;
            const bPriority = statusPriority[bStatus] || 99;
            comparison = aPriority - bPriority;
            break;
          }
          case 'name':
            comparison = a.unit_code.localeCompare(b.unit_code);
            break;
          case 'checkins': {
            const aCheckins = unitCounts[a.id]?.checkin_count ?? 0;
            const bCheckins = unitCounts[b.id]?.checkin_count ?? 0;
            comparison = bCheckins - aCheckins;
            break;
          }
          case 'workorders': {
            const aOrders = unitCounts[a.id]?.open_work_orders ?? 0;
            const bOrders = unitCounts[b.id]?.open_work_orders ?? 0;
            comparison = bOrders - aOrders;
            break;
          }
          case 'maintenance': {
            const aMaintenanceDue = unitCounts[a.id]?.maintenance_due ?? 0;
            const bMaintenanceDue = unitCounts[b.id]?.maintenance_due ?? 0;
            comparison = bMaintenanceDue - aMaintenanceDue;
            break;
          }
          case 'alerts': {
            const aAlerts = (a.fault_count || 0) + (a.warning_count || 0);
            const bAlerts = (b.fault_count || 0) + (b.warning_count || 0);
            comparison = bAlerts - aAlerts;
            break;
          }
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    },
    [sortField, sortDirection, getStatusInfo, unitCounts]
  );

  // Toggle sort field
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Toggle group expand/collapse
  const toggleGroup = useCallback(
    (groupId: string) => {
      // Prevent collapsing the single top-level group
      if (hasSingleTopLevelGroup && groupId === singleGroupId) {
        return;
      }

      setExpandedGroups((prev) => {
        const next = new Set(prev || []);
        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        return next;
      });
    },
    [hasSingleTopLevelGroup, singleGroupId]
  );

  // Toggle industry section (superadmins)
  const toggleIndustry = useCallback((industry: IndustryType) => {
    setCollapsedIndustries((prev) => {
      const next = new Set(prev);
      if (next.has(industry)) {
        next.delete(industry);
      } else {
        next.add(industry);
      }
      return next;
    });
  }, []);

  // Toggle all groups
  const toggleAllGroups = useCallback(() => {
    if (allGroupsExpanded) {
      // Collapse all groups EXCEPT single top-level company
      if (hasSingleTopLevelGroup && singleGroupId) {
        setExpandedGroups(new Set([singleGroupId]));
      } else {
        setExpandedGroups(new Set());
      }
      // For superadmins: collapse all industry dividers
      if (permissions.canSeeCompanies) {
        setCollapsedIndustries(new Set(INDUSTRY_TYPE_ORDER));
      }
    } else {
      const allIds = new Set<string>();
      const collectIds = (nodes: HierarchicalGroupNode[]) => {
        nodes.forEach((node) => {
          allIds.add(node.id);
          if (node.children) collectIds(node.children);
        });
      };
      collectIds(hierarchicalGroups);
      setExpandedGroups(allIds);
      // For superadmins: expand all industry dividers
      if (permissions.canSeeCompanies) {
        setCollapsedIndustries(new Set());
      }
    }
    setAllGroupsExpanded(!allGroupsExpanded);
  }, [allGroupsExpanded, hierarchicalGroups, hasSingleTopLevelGroup, singleGroupId, permissions.canSeeCompanies]);

  // Handle unit click - navigate to unit records page
  const handleUnitClick = useCallback(
    (unit: Unit) => {
      navigate(`/records/${unit.unit_code}`);
    },
    [navigate]
  );

  // Check if a unit is currently selected (based on URL path /records/:unitCode)
  const selectedUnitCode = useMemo(() => {
    const staticPaths = ['inventory', 'compliance', 'maintenance', 'work-orders', 'check-ins'];
    const match = location.pathname.match(/^\/records\/([^/]+)/);
    if (match && !staticPaths.includes(match[1])) {
      return match[1];
    }
    return null;
  }, [location.pathname]);

  // Render a unit list item - matches GroupedUnitList styling exactly
  const renderUnitItem = (unit: Unit, _level: number = 0) => {
    const statusInfo = getStatusInfo(unit.id);
    const isSelected = unit.unit_code === selectedUnitCode;

    // Get counts from fetched unit counts data
    const unitCountData = unitCounts[unit.id];
    const checkinCount = unitCountData?.checkin_count ?? 0;
    const workOrderCount = unitCountData?.open_work_orders ?? 0;
    const maintenanceCount = unitCountData?.maintenance_due ?? 0;

    // Alert counts from status info or unit
    const faultCount = statusInfo?.fault_count ?? unit.fault_count ?? 0;
    const warningCount = statusInfo?.warning_count ?? unit.warning_count ?? 0;

    return (
      <button
        key={unit.id}
        data-unit-id={unit.id}
        onClick={() => handleUnitClick(unit)}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-all text-left border-b border-border/50',
          isSelected && 'bg-[#8FB560]/10 border-l-2 border-l-[#8FB560]'
        )}
      >
        {/* Operational Status Indicator */}
        <div className="w-6 flex justify-center flex-shrink-0">
          <div
            className={cn(
              'w-2.5 h-2.5 rounded-full',
              unit.operationalStatus === 'active' && 'bg-blue-500',
              unit.operationalStatus === 'maintenance' && 'bg-purple-500',
              unit.operationalStatus === 'off_rent' && 'bg-amber-500',
              unit.operationalStatus === 'inactive' && 'bg-muted-foreground/50',
              !unit.operationalStatus && 'bg-blue-500'
            )}
            title={unit.operationalStatus || 'active'}
          />
        </div>

        {/* Unit breadcrumb: Code · Name · Location · Group */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={cn('text-sm font-medium', isSelected && 'text-[#8FB560]')}>{unit.unit_code}</span>
            {/* Dual alert icons: warning (triangle) and fault (circle) */}
            {warningCount > 0 && <AlertTriangle className="h-3 w-3 flex-shrink-0 text-warning" />}
            {faultCount > 0 && <AlertCircle className="h-3 w-3 flex-shrink-0 text-fault" />}
          </div>
          <div className={cn('text-[10px] text-muted-foreground/60 truncate', getLocationTextClass(unit.locationSource))}>
            {[
              unit.name && unit.name !== unit.unit_code && !unit.name.includes(unit.unit_code) ? unit.name : null,
              formatLocationShort(unit.location?.address),
            ].filter(Boolean).join(' · ')}
          </div>
        </div>

        {/* Records-specific counts with icons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Check-ins */}
          <div className="flex items-center gap-0.5" title="Check-ins">
            <LogIn className="h-3 w-3 text-muted-foreground/50" />
            <span className="text-[10px] tabular-nums text-muted-foreground min-w-[14px] text-right">
              {checkinCount}
            </span>
          </div>

          {/* Work Orders */}
          <div className="flex items-center gap-0.5" title="Work Orders">
            <ClipboardList
              className={cn('h-3 w-3', workOrderCount > 0 ? 'text-amber-400' : 'text-muted-foreground/50')}
            />
            <span
              className={cn(
                'text-[10px] tabular-nums min-w-[14px] text-right',
                workOrderCount > 0 ? 'text-amber-400 font-medium' : 'text-muted-foreground'
              )}
            >
              {workOrderCount}
            </span>
          </div>

          {/* Maintenance */}
          <div className="flex items-center gap-0.5" title="Maintenance Due">
            <Wrench className={cn('h-3 w-3', maintenanceCount > 0 ? 'text-orange-400' : 'text-muted-foreground/50')} />
            <span
              className={cn(
                'text-[10px] tabular-nums min-w-[14px] text-right',
                maintenanceCount > 0 ? 'text-orange-400 font-medium' : 'text-muted-foreground'
              )}
            >
              {maintenanceCount}
            </span>
          </div>

          {/* Alerts */}
          <div className="flex items-center gap-0.5" title="Alerts">
            <AlertTriangle
              className={cn('h-3 w-3', faultCount + warningCount > 0 ? 'text-red-400' : 'text-muted-foreground/50')}
            />
            <span
              className={cn(
                'text-[10px] tabular-nums min-w-[14px] text-right',
                faultCount + warningCount > 0 ? 'text-red-400 font-medium' : 'text-muted-foreground'
              )}
            >
              {faultCount + warningCount}
            </span>
          </div>
        </div>
      </button>
    );
  };

  // Render a group header - matches GroupDivider styling exactly
  const renderGroupHeader = (node: HierarchicalGroupNode, level: number = 0) => {
    const isExpanded = expandedGroups === null || expandedGroups.has(node.id);
    const unitCount = node.unitCount || node.units.length;
    const indentPx = level * 16;
    const isUnassigned = node.name === 'Unassigned';
    const isOnlySingleTopLevel = hasSingleTopLevelGroup && level === 0;

    return (
      <div className="group/divider">
        <Separator className="mt-0.5" />
        <div
          onClick={() => toggleGroup(node.id)}
          className={cn(
            'flex items-center justify-between px-2 py-0.5 transition-all cursor-pointer sticky top-0 z-10 relative',
            // Hierarchical background colors based on indent level (matching GroupDivider)
            // Level 0 (Company) - Darkest
            level === 0 &&
              (isUnassigned ? 'bg-muted/60 hover:bg-muted/80' : 'bg-muted/95 hover:bg-muted'),
            // Level 1 (Production) - Medium
            level === 1 &&
              (isUnassigned ? 'bg-muted/35 hover:bg-muted/55' : 'bg-muted/55 hover:bg-muted/75'),
            // Level 2+ (Site) - Lightest
            level >= 2 && (isUnassigned ? 'bg-muted/10 hover:bg-muted/30' : 'bg-muted/20 hover:bg-muted/40')
          )}
          style={{
            paddingLeft: `${indentPx + 8}px`,
          }}
        >
          {/* Left side: Chevron + Group name + Unit count */}
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {!isOnlySingleTopLevel ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(node.id);
                }}
                className="p-0.5 hover:bg-muted rounded transition-colors flex-shrink-0"
                aria-label={isExpanded ? 'Collapse group' : 'Expand group'}
                aria-expanded={isExpanded}
              >
                <motion.div animate={{ rotate: isExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </motion.div>
              </button>
            ) : (
              <div className="w-4 flex-shrink-0" />
            )}

            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span
                className={cn(
                  'text-[10px] uppercase tracking-wider truncate transition-colors text-muted-foreground',
                  level === 0 ? 'font-bold' : 'font-medium',
                  level >= 2 && !isUnassigned && 'italic',
                  isUnassigned && 'italic opacity-70'
                )}
              >
                {node.name}
              </span>
              <span
                className={cn(
                  'text-xs flex-shrink-0 transition-colors text-muted-foreground/70',
                  level === 0 && 'font-semibold'
                )}
              >
                ({unitCount})
              </span>
            </div>
          </div>

          {/* Right side: Placeholder for map pin icon (matches GroupDivider structure) */}
          <div className="p-1 opacity-0 flex-shrink-0">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  };

  // Render industry section header for superadmins (matches GroupedUnitList)
  const renderIndustryHeader = (industry: IndustryType, count: number) => {
    const isCollapsed = collapsedIndustries.has(industry);
    return (
      <button
        key={`industry-header-${industry}`}
        onClick={() => toggleIndustry(industry)}
        className="w-full flex items-center justify-between px-3 py-2 bg-muted/50 border-b border-border/50 hover:bg-muted/70 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {INDUSTRY_TYPE_LABELS[industry] || industry.toUpperCase()}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">({count})</span>
      </button>
    );
  };

  // Render group recursively
  const renderGroupNode = (node: HierarchicalGroupNode, level: number = 0): React.ReactNode => {
    const isExpanded = expandedGroups === null || expandedGroups.has(node.id);
    const sortedUnits = sortUnits(node.units);

    return (
      <div key={node.id}>
        {renderGroupHeader(node, level)}

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden' }}
            >
              {/* Render children groups first */}
              {node.children && node.children.length > 0 && (
                <div>{node.children.map((child) => renderGroupNode(child, level + 1))}</div>
              )}

              {/* Render units if this is a leaf node */}
              {(!node.children || node.children.length === 0) && sortedUnits.map((unit) => renderUnitItem(unit, level))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Collapsed state - show nothing (navigation icons handle this)
  if (collapsed) {
    return null;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sort Header - matches Sidebar.tsx header styling */}
      <div className="flex-none px-4 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {/* Status Sort Button */}
          <button
            onClick={() => handleSort('status')}
            className="w-6 flex items-center justify-center hover:text-foreground transition-colors relative -ml-1"
            title="Sort by Status"
          >
            <Activity className="h-3 w-3" />
            {sortField === 'status' && (
              <ChevronDown
                className={cn('h-2 w-2 absolute translate-y-2', sortDirection === 'asc' && 'rotate-180')}
              />
            )}
          </button>

          {/* UNITS label with count - clickable for name sorting */}
          <button
            onClick={() => handleSort('name')}
            className="flex-1 min-w-0 text-left hover:text-foreground transition-colors"
            title="Sort by Name"
          >
            {filteredUnits.length !== totalUnitsCount ? (
              <>
                <span className="text-[#8FB560] font-semibold">{filteredUnits.length}</span>
                <span>/{totalUnitsCount}</span>
                <span> UNITS</span>
              </>
            ) : (
              <span>{filteredUnits.length} UNITS</span>
            )}
            {sortField === 'name' && (
              <ChevronDown
                className={cn('h-2 w-2 inline-block ml-1', sortDirection === 'asc' && 'rotate-180')}
              />
            )}
          </button>

          {/* Expand/Collapse All Groups Toggle */}
          <button
            onClick={toggleAllGroups}
            className="flex items-center gap-0.5 text-[#8FB560] hover:text-[#7fa050] transition-colors mr-3"
            title={allGroupsExpanded ? 'Collapse all groups' : 'Expand all groups'}
          >
            {allGroupsExpanded ? <ChevronsUp className="h-3 w-3" /> : <ChevronsDown className="h-3 w-3" />}
            <span className="text-[8px] font-semibold uppercase tracking-tight leading-none">ALL</span>
          </button>

          {/* Records Icons Group - aligned to match unit row icon positions */}
          {/* Each button width (w-[28px]) matches unit row icon+number groups */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => handleSort('checkins')}
              className="w-[28px] flex items-center justify-center hover:text-foreground transition-colors relative"
              title="Sort by Check-ins"
            >
              <LogIn className="h-3 w-3" />
              {sortField === 'checkins' && (
                <ChevronDown
                  className={cn('h-2 w-2 absolute translate-y-2', sortDirection === 'asc' && 'rotate-180')}
                />
              )}
            </button>
            <button
              onClick={() => handleSort('workorders')}
              className="w-[28px] flex items-center justify-center hover:text-foreground transition-colors relative"
              title="Sort by Work Orders"
            >
              <ClipboardList className="h-3 w-3" />
              {sortField === 'workorders' && (
                <ChevronDown
                  className={cn('h-2 w-2 absolute translate-y-2', sortDirection === 'asc' && 'rotate-180')}
                />
              )}
            </button>
            <button
              onClick={() => handleSort('maintenance')}
              className="w-[28px] flex items-center justify-center hover:text-foreground transition-colors relative"
              title="Sort by Maintenance"
            >
              <Wrench className="h-3 w-3" />
              {sortField === 'maintenance' && (
                <ChevronDown
                  className={cn('h-2 w-2 absolute translate-y-2', sortDirection === 'asc' && 'rotate-180')}
                />
              )}
            </button>
            <button
              onClick={() => handleSort('alerts')}
              className="w-[28px] flex items-center justify-center hover:text-foreground transition-colors relative"
              title="Sort by Alerts"
            >
              <AlertTriangle className="h-3 w-3" />
              {sortField === 'alerts' && (
                <ChevronDown
                  className={cn('h-2 w-2 absolute translate-y-2', sortDirection === 'asc' && 'rotate-180')}
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Unit List - scrollable area */}
      <div className="flex-1 overflow-y-auto">
        {/* Superadmin view: Group companies by industry with collapsible sections */}
        {permissions.canSeeCompanies && groupedByIndustry ? (
          <>
            {INDUSTRY_TYPE_ORDER.map((industry) => {
              const industryGroups = groupedByIndustry[industry];
              if (!industryGroups || industryGroups.length === 0) return null;

              const isCollapsed = collapsedIndustries.has(industry);
              const totalUnits = industryGroups.reduce((sum, g) => sum + g.unitCount, 0);

              return (
                <div key={industry}>
                  {renderIndustryHeader(industry, totalUnits)}
                  <AnimatePresence initial={false}>
                    {!isCollapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {industryGroups.map((group) => renderGroupNode(group, 0))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </>
        ) : (
          /* Regular view: Show hierarchy directly */
          hierarchicalGroups.map((group) => renderGroupNode(group, 0))
        )}

        {hierarchicalGroups.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">No units match current filters</div>
        )}
      </div>
    </div>
  );
}
